type RegistrationState = {
  documentNumber: string
  dactilar: string
  country: 'EC' | 'PE' | 'US' | null
  countryResidenceCode: string | null
  flowStep: 'consent' | 'country' | 'identity' | 'biometric' | 'otp-contact' | 'otp-code' | 'password' | 'success' | null
  biometricToken: string | null
  biometricCodigoId: string | null
  biometricCorrelationId: string | null
  biometricRequestId: string | null
  biometricProcessId: number | null
  biometricCompleted: boolean
  correlationId: string | null
  phone: string
  email: string
  expiresInSeconds: number
  resendsRemaining: number
}

const listeners = new Set<() => void>()

let state: RegistrationState = {
  documentNumber: '',
  dactilar: '',
  country: null,
  countryResidenceCode: null,
  flowStep: null,
  biometricToken: null,
  biometricCodigoId: null,
  biometricCorrelationId: null,
  biometricRequestId: null,
  biometricProcessId: null,
  biometricCompleted: false,
  correlationId: null,
  phone: '',
  email: '',
  expiresInSeconds: 60,
  resendsRemaining: 3,
}

function emitChange() {
  listeners.forEach((listener) => listener())
}

export function getRegistrationState(): RegistrationState {
  return state
}

export function setCorrelationId(correlationId: string) {
  state = { ...state, correlationId }
  emitChange()
}

const COUNTRY_RESIDENCE_CODES: Record<'EC' | 'PE' | 'US', string> = {
  EC: 'ECU',
  PE: 'PER',
  US: 'USA',
}

export function setCountry(country: 'EC' | 'PE' | 'US') {
  state = {
    ...state,
    country,
    countryResidenceCode: COUNTRY_RESIDENCE_CODES[country],
    flowStep: 'identity',
  }
  emitChange()
}

export function setFlowStep(flowStep: NonNullable<RegistrationState['flowStep']>) {
  state = { ...state, flowStep }
  emitChange()
}

export function setIdentityData(documentNumber: string, dactilar: string) {
  state = { ...state, documentNumber, dactilar }
  emitChange()
}

export function setBiometricData(
  token: string,
  codigoId: string,
  correlationId: string,
  requestId: string,
  processId?: number | null,
) {
  state = {
    ...state,
    biometricToken: token,
    biometricCodigoId: codigoId,
    biometricCorrelationId: correlationId,
    biometricRequestId: requestId,
    biometricProcessId: processId ?? null,
  }
  emitChange()
}

export function setRegistrationStarted(correlationId: string, phone: string, email: string, expiresInSeconds = 60) {
  state = { ...state, correlationId, phone, email, expiresInSeconds, flowStep: 'otp-contact' }
  emitChange()
}

export function setResendsRemaining(count: number) {
  state = { ...state, resendsRemaining: count }
  emitChange()
}

export function markBiometricCompleted() {
  state = { ...state, biometricCompleted: true, flowStep: 'success' }
  emitChange()
}

export function resetRegistration() {
  state = {
    documentNumber: '',
    dactilar: '',
    country: null,
    countryResidenceCode: null,
    flowStep: 'consent',
    biometricToken: null,
    biometricCodigoId: null,
    biometricCorrelationId: null,
    biometricRequestId: null,
    biometricProcessId: null,
    biometricCompleted: false,
    correlationId: null,
    phone: '',
    email: '',
    expiresInSeconds: 60,
    resendsRemaining: 3,
  }
  emitChange()
}

export function subscribeRegistration(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
